"use client";

import type { ComponentProps, ReactElement, ReactNode } from "react";

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@tripcart/traveler-ui/components/sheet";
import { Button } from "@tripcart/traveler-ui/components/button";

import { LazyPopover } from "@/components/lazy-floating/popover.client";

import { useIsDesktop } from "../_use-is-desktop.client";

import styles from "./_field-overlay.module.css";

/**
 * Where a booking field opens: a bottom SHEET on a phone, an anchored popover
 * from `lg`.
 *
 * The mobile guide opens every picker on this page the same way — one sheet
 * shell, a title, the control, and a full-width button to dismiss it. Doing it
 * per-field was how the date picker and the traveller picker ended up
 * disagreeing: the calendar became a sheet and the travellers stayed a popover,
 * so two fields stacked in one card behaved differently on the same tap.
 *
 * `lg` is the same 1024px the page grid, the pinned price bar and the bottom
 * tab bar use — `useIsDesktop` is where that number lives for the whole route.
 * The trigger is rendered by the SAME callback in both branches, so hydration
 * has nothing to reconcile when the phone guess turns out wrong.
 */

export interface FieldOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sheet heading. The popover has no header — it is anchored to a labelled
   *  field, so on desktop the label is already directly above it. */
  title: string;
  /** Label for the sheet's dismiss button. The POPOVER branch renders no
   *  footer — on desktop the field's own body already carries one, sized for
   *  a menu rather than for a thumb. */
  doneLabel: string;
  /**
   * Renders the field.
   *
   * Called with `triggerProps` on desktop (the popover's anchor handlers) and
   * with NOTHING on a phone, where there is no anchor. A trigger written for
   * both must therefore supply its own `onClick` when `triggerProps` is
   * undefined — see `DateTrigger` for the shape.
   */
  renderTrigger: (triggerProps?: Record<string, unknown>) => ReactElement;
  /** Passed straight to `LazyPopover` on desktop; ignored on a phone. */
  popover?: {
    side?: "top" | "right" | "bottom" | "left";
    align?: "start" | "center" | "end";
    sideOffset?: number;
    contentClassName?: string;
    collisionAvoidance?: ComponentProps<
      typeof LazyPopover
    >["collisionAvoidance"];
  };
  children: ReactNode;
}

export function FieldOverlay({
  open,
  onOpenChange,
  title,
  doneLabel,
  renderTrigger,
  popover,
  children,
}: FieldOverlayProps) {
  const isDesktop = useIsDesktop();

  if (!isDesktop) {
    return (
      <>
        {renderTrigger()}
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent side="bottom">
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
            </SheetHeader>
            <SheetBody>{children}</SheetBody>
            {/* The guide's `#tvdDone` — full width at the foot of the sheet.
                It COMMITS NOTHING: every stepper inside writes straight
                through, and the figures behind the sheet are already live. Its
                job is dismissal, which on a phone otherwise means reaching
                past the sheet for the scrim.
                AMBER (`variant="book"` = the guide's `--cta`), because that is
                what the guide draws here (`btn btn-cta btn-full`) and because
                on a phone this is the only control in the sheet — there is no
                neighbouring CTA for it to outrank, which is the objection that
                keeps the DESKTOP popover's button a small outline. If you would
                rather amber stayed strictly on date commits, this is the one
                line to change. */}
            <SheetFooter>
              <Button
                type="button"
                variant="book"
                size="lg"
                className={styles.done}
                onClick={() => onOpenChange(false)}
              >
                {doneLabel}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <LazyPopover
      open={open}
      onOpenChange={onOpenChange}
      side={popover?.side}
      align={popover?.align}
      sideOffset={popover?.sideOffset}
      contentClassName={popover?.contentClassName}
      collisionAvoidance={popover?.collisionAvoidance}
      renderTrigger={(triggerProps) => renderTrigger(triggerProps)}
    >
      {children}
    </LazyPopover>
  );
}
